<template>
  <div class="address-contacts">
    <van-nav-bar
      :fixed="true"
      z-index="9"
      :title="$t('accountPage.addressesContacts')"
      :border="false"
      left-arrow
      @click-left="goBack"
    />

    <main class="address-contacts__content">
      <section class="address-contacts__tabs">
        <button
          type="button"
          :class="{ active: activeTab === 'address' }"
          @click="activeTab = 'address'"
        >
          <van-icon name="location" />
          <span>{{ $t('addressContacts.addresses') }}</span>
        </button>
        <button
          type="button"
          :class="{ active: activeTab === 'contact' }"
          @click="activeTab = 'contact'"
        >
          <van-icon name="contact" />
          <span>{{ $t('addressContacts.contacts') }}</span>
        </button>
      </section>

      <section v-if="activeTab === 'address'" class="address-contacts__panel">
        <button v-if="addressList.length" class="address-contacts__primary" type="button" @click="openAddAddress">
          {{ $t('addressPop.add') }}
        </button>
        <div v-else class="address-contacts__empty">
          <van-icon name="location" />
          <h2>{{ $t('addressContacts.emptyAddresses') }}</h2>
          <p>{{ $t('addressContacts.emptyAddressesDesc') }}</p>
          <button type="button" @click="openAddAddress">{{ $t('addressPop.add') }}</button>
        </div>

        <article v-for="address in addressList" :key="address.id" class="address-card">
          <div class="address-card__body">
            <h3>{{ address.recipientName }}</h3>
            <p>{{ formatAddressPhone(address.recipientPhone) }}</p>
            <p>{{ addressLine(address) }}</p>
          </div>
          <div class="address-card__divider"></div>
          <div class="address-card__actions">
            <button type="button" class="address-card__default" @click="confirmSetDefaultAddress(address)">
              <span class="address-card__radio" :class="{ checked: address.isDefault }"></span>
              <span>{{ $t('default') }}</span>
            </button>
            <div class="address-card__right-actions">
              <button type="button" class="address-card__edit" @click="openEditAddress(address)">
                <van-icon name="edit" />
                <span>{{ $t('edit') }}</span>
              </button>
              <span class="address-card__action-divider"></span>
              <button type="button" class="address-card__delete" @click="confirmDeleteAddress(address)">
                <van-icon name="delete" />
                <span>{{ $t('delete') }}</span>
              </button>
            </div>
          </div>
        </article>
      </section>

      <section v-else class="address-contacts__panel">
        <button v-if="contactList.length" class="address-contacts__primary" type="button" @click="openAddContact">
          {{ $t('addressContacts.addContact') }}
        </button>
        <div v-else class="address-contacts__empty">
          <van-icon name="contact" />
          <h2>{{ $t('addressContacts.emptyContacts') }}</h2>
          <p>{{ $t('addressContacts.emptyContactsDesc') }}</p>
          <button type="button" @click="openAddContact">{{ $t('addressContacts.goAdd') }}</button>
        </div>

        <article v-for="contact in contactList" :key="contact.id" class="contact-manage-card">
          <van-icon class="contact-manage-card__avatar" name="manager" />
          <div class="contact-manage-card__body">
            <h3>{{ contactName(contact) }}</h3>
            <div class="contact-manage-card__bottom">
              <p>{{ displayContactPhone(contact) }}</p>
              <div class="contact-manage-card__actions">
                <button type="button" @click="openEditContact(contact)">
                  <van-icon name="edit" />
                  <span>{{ $t('edit') }}</span>
                </button>
                <span></span>
                <button type="button" class="danger" @click="confirmDeleteContact(contact)">
                  <van-icon name="delete" />
                  <span>{{ $t('delete') }}</span>
                </button>
              </div>
            </div>
          </div>
        </article>

        <div class="contact-notice">
          <van-icon name="info" />
          <span>{{ $t('addressContacts.contactUsageNotice') }}</span>
        </div>
      </section>
    </main>

    <AddressPopup ref="addressPopup" @change="reloadAddresses" @delete="reloadAddresses" />
    <ContactPopup
      ref="contactPopup"
      management-mode
      :need-count="needContactCount"
      :user-phone="userInfo.phoneNumber"
      @contacts-changed="onContactsChanged"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { Dialog, Icon, NavBar } from 'vant';
import { LANGUAGE, RELATIONSHIP_TYPE } from '@/common/constants';
import { deleteContact, getContactCount, queryContactList } from '@/api/order';
import AddressPopup from '@/components/AddressPopup/index.vue';
import ContactPopup from '@/components/ContactPopup/index.vue';
import routerBack from '@/mixins/routerBack';
import { formatAddressForList } from '@/utils/address';

export default {
  name: 'AddressContacts',
  mixins: [routerBack],
  components: {
    AddressPopup,
    ContactPopup,
    [Icon.name]: Icon,
    [NavBar.name]: NavBar,
  },
  data() {
    return {
      activeTab: 'address',
      contactList: [],
      needContactCount: 1,
    };
  },
  computed: {
    ...mapState('user', ['addressList', 'userInfo']),
    ...mapState('orders', ['emergencyContactList', 'tempOrderData']),
  },
  mounted() {
    this.loadPage();
  },
  methods: {
    ...mapActions('user', ['deleteAddress', 'getAddressList', 'setDefaultAddress']),
    ...mapActions('orders', ['changeAddress', 'changeContactList']),
    goBack() {
      this.$router.push('/profile/index');
    },
    async loadPage() {
      await this.reloadAddresses();
      await Promise.all([this.loadContactCount(), this.loadContacts()]);
    },
    async reloadAddresses() {
      await this.getAddressList();
    },
    async loadContactCount() {
      const res = await getContactCount();
      if (res?.code === 'Success') {
        this.needContactCount = Math.max(Number(res.data?.count) || 1, 1);
      }
    },
    async loadContacts() {
      const res = await queryContactList();
      if (res?.code !== 'Success') {
        this.$toast(res?.message || this.$t('systemError'));
        return;
      }
      const records = res.data?.records || res.data?.list || res.data || [];
      this.contactList = Array.isArray(records) ? records.map(this.normalizeContact) : [];
    },
    onContactsChanged({ data }) {
      this.contactList = Array.isArray(data) ? data.map(this.normalizeContact) : [];
    },
    openAddAddress() {
      this.$refs.addressPopup.open({ showType: 1 });
    },
    openEditAddress(address) {
      this.$refs.addressPopup.open({ showType: 1, editAddress: address });
    },
    confirmDeleteAddress(address) {
      Dialog.confirm({
        title: this.$t('addressPop.deleteTitle'),
        message: this.$t('addressPop.deleteConfirm'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      }).then(async () => {
        const res = await this.deleteAddress({ id: address.id });
        if (res?.code === 'Success') {
          this.syncDeletedAddress(address.id);
        }
      }).catch(() => {});
    },
    confirmSetDefaultAddress(address) {
      if (address.isDefault) return;
      Dialog.confirm({
        title: this.$t('addressContacts.setDefaultAddressTitle'),
        message: this.$t('addressContacts.setDefaultAddressConfirm'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      }).then(async () => {
        const res = await this.setDefaultAddress({ id: address.id });
        if (res?.code === 'Success') {
          await this.reloadAddresses();
        }
      }).catch(() => {});
    },
    openAddContact() {
      this.$refs.contactPopup.open({ startCreate: true });
    },
    openEditContact(contact) {
      this.$refs.contactPopup.open({ editContact: contact });
    },
    confirmDeleteContact(contact) {
      Dialog.confirm({
        title: this.$t('contactPop.deleteTitle'),
        message: this.$t('contactPop.deleteConfirm'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      }).then(async () => {
        const res = await deleteContact(contact.id);
        if (res?.code !== 'Success') {
          this.$toast(res?.message || this.$t('systemError'));
          return;
        }
        this.contactList = this.contactList.filter(item => item.id !== contact.id);
        this.syncDeletedContact(contact.id);
      }).catch(() => {});
    },
    syncDeletedAddress(addressId) {
      if (!addressId || String(this.tempOrderData?.addressId || '') !== String(addressId)) return;
      const fallbackAddress = this.addressList.find(item => item.isDefault) || this.addressList[0] || null;
      this.changeAddress(fallbackAddress);
    },
    syncDeletedContact(contactId) {
      if (!contactId) return;
      const deletedId = String(contactId);
      const contactMap = new Map();
      [
        ...(this.tempOrderData?.emergencyContactList || []),
        ...(this.emergencyContactList || [])
      ].forEach(item => {
        const id = item?.id || item?.contactId;
        if (id !== undefined && id !== null) contactMap.set(String(id), item);
      });
      const selectedContacts = Array.from(contactMap.values());
      const updatedContacts = selectedContacts.filter(item => {
        const id = item.id || item.contactId;
        return String(id) !== deletedId;
      });
      if (updatedContacts.length !== selectedContacts.length) {
        this.changeContactList(updatedContacts);
      }
    },
    addressLine(address) {
      return formatAddressForList(address);
    },
    formatAddressPhone(phone = '') {
      const value = String(phone || '').replace(/\D/g, '');
      const local = value.startsWith('62') ? value.slice(2) : value.replace(/^0/, '');
      return local ? `+62 ${local}` : '';
    },
    normalizePhone(contact = {}) {
      const phone = String(contact.phone || contact.mobile || '').replace(/\D/g, '');
      return phone.startsWith('62') ? phone.slice(2) : phone.replace(/^0/, '');
    },
    normalizeContact(contact = {}) {
      const relationship = contact.relation?.value || contact.relationship || '';
      return {
        ...contact,
        id: contact.id || contact.contactId || '',
        phone: this.normalizePhone(contact),
        relationship,
        relation: RELATIONSHIP_TYPE.find(item => item.value === relationship) || null,
      };
    },
    relationText(contact) {
      const relation = contact.relation || RELATIONSHIP_TYPE.find(item => item.value === contact.relationship);
      if (!relation) return '';
      if (this.$i18n.locale === LANGUAGE.id) return relation.textId;
      if (this.$i18n.locale === LANGUAGE.ph) return relation.textPh;
      return relation.textEn;
    },
    contactName(contact) {
      const relation = this.relationText(contact);
      return relation ? `${contact.name} (${relation})` : contact.name;
    },
    displayContactPhone(contact) {
      const phone = this.normalizePhone(contact);
      if (!phone) return '';
      const groups = [phone.slice(0, 3)];
      for (let index = 3; index < phone.length; index += 4) {
        groups.push(phone.slice(index, index + 4));
      }
      return `+62 ${groups.filter(Boolean).join(' ')}`;
    },
  },
};
</script>

<style lang="less" scoped>
.address-contacts {
  min-height: 100vh;
  background: #f4f8ff;
  color: #070d3f;

  button {
    font-family: inherit;
  }
}

.address-contacts__content {
  padding: 112px 8px 40px;
  box-sizing: border-box;
}

.address-contacts__tabs {
  display: flex;
  background: #ffffff;
  border-bottom: 2px solid #e5eaf3;

  button {
    position: relative;
    display: flex;
    flex: 1;
    height: 96px;
    align-items: center;
    justify-content: center;
    gap: 16px;
    border: 0;
    background: #ffffff;
    color: #6b7280;
    font-size: 32px;
    font-weight: 600;
    line-height: 44px;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -2px;
      height: 6px;
      background: transparent;
    }

    &.active {
      color: #1454ff;

      &::after {
        background: #1454ff;
      }
    }
  }

  .van-icon {
    font-size: 32px;
  }
}

.address-contacts__panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 18px;
}

.address-contacts__primary,
.address-contacts__empty button {
  width: 100%;
  height: 96px;
  border: 0;
  border-radius: 18px;
  background: #1454ff;
  color: #ffffff;
  font-size: 32px;
  font-weight: 500;
  line-height: 96px;
}

.address-contacts__empty {
  display: flex;
  min-height: 360px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  border: 2px solid #e5eaf3;
  border-radius: 18px;
  background: #ffffff;
  text-align: center;

  > .van-icon {
    color: #1454ff;
    font-size: 68px;
  }

  h2 {
    margin: 24px 0 0;
    font-size: 30px;
    font-weight: 500;
    line-height: 40px;
  }

  p {
    margin: 12px 0 32px;
    color: #6b7280;
    font-size: 26px;
    font-weight: 400;
    line-height: 36px;
  }
}

.address-card,
.contact-manage-card,
.contact-notice {
  border: 2px solid #dbe4f0;
  border-radius: 18px;
  background: #ffffff;
}

.address-card {
  padding: 26px 28px 0;

  h3 {
    margin: 0 0 12px;
    font-size: 26px;
    font-weight: 400;
    line-height: 36px;
  }

  p {
    margin: 8px 0 0;
    color: #6b7280;
    font-size: 24px;
    font-weight: 400;
    line-height: 34px;
  }
}

.address-card__divider {
  height: 2px;
  margin-top: 24px;
  background: #e5eaf3;
}

.address-card__actions {
  display: flex;
  min-height: 78px;
  align-items: center;
  justify-content: space-between;

  button {
    display: flex;
    align-items: center;
    border: 0;
    background: transparent;
    font-size: 24px;
    font-weight: 400;
    line-height: 34px;
  }
}

.address-card__default {
  gap: 14px;
  color: #6b7280;
}

.address-card__radio {
  position: relative;
  width: 34px;
  height: 34px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  box-sizing: border-box;
  background: #ffffff;

  &.checked {
    border-color: #1454ff;
    background: #1454ff;

    &::after {
      content: '';
      position: absolute;
      left: 9px;
      top: 6px;
      width: 10px;
      height: 16px;
      border: solid #ffffff;
      border-width: 0 3px 3px 0;
      transform: rotate(45deg);
    }
  }
}

.address-card__right-actions,
.contact-manage-card__actions {
  display: flex;
  align-items: center;
}

.address-card__edit,
.contact-manage-card__actions button {
  gap: 8px;
  color: #1454ff;
}

.address-card__delete,
.contact-manage-card__actions .danger {
  gap: 8px;
  color: #ff2b20;
}

.address-card__action-divider,
.contact-manage-card__actions > span {
  width: 2px;
  height: 34px;
  margin: 0 28px;
  background: #dbe4f0;
}

.contact-manage-card {
  display: flex;
  align-items: center;
  padding: 28px;
}

.contact-manage-card__avatar {
  flex: 0 0 auto;
  color: #1454ff;
  font-size: 56px;
}

.contact-manage-card__body {
  min-width: 0;
  flex: 1;
  margin-left: 24px;

  h3 {
    margin: 0;
    color: #070d3f;
    font-size: 28px;
    font-weight: 400;
    line-height: 38px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.contact-manage-card__bottom {
  display: flex;
  align-items: center;
  margin-top: 12px;

  p {
    min-width: 0;
    flex: 1;
    margin: 0;
    color: #070d3f;
    font-size: 26px;
    font-weight: 400;
    line-height: 36px;
  }
}

.contact-manage-card__actions {
  margin-left: 16px;

  button {
    border: 0;
    background: transparent;
    font-size: 24px;
    font-weight: 400;
    line-height: 34px;
  }
}

.contact-notice {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px 28px;
  background: #eaf2ff;
  color: #070d3f;
  font-size: 24px;
  font-weight: 400;
  line-height: 34px;

  .van-icon {
    flex: 0 0 auto;
    color: #1454ff;
    font-size: 36px;
  }
}

::v-deep .van-nav-bar {
  height: 96px;
}

::v-deep .van-nav-bar__content {
  height: 96px;
}

::v-deep .van-nav-bar__title {
  max-width: calc(100% - 176px);
  color: #070d3f;
  font-size: 36px;
  font-weight: 500;
  line-height: 52px;
}

::v-deep .van-nav-bar__left {
  display: flex;
  align-items: center;
  width: 88px;
  height: 96px;
  padding: 0;
}

::v-deep .van-nav-bar__arrow {
  margin-left: 16px;
  color: #070d3f;
  font-size: 56px;
}
</style>
